import { useRef } from 'react';
import { Button, Image, ListGroup, Stack } from 'react-bootstrap';
import { isMobile } from 'react-device-detect';
import useAvatarImageFallback from '@hooks/useAvatarImageFallback';
import { ImPhone } from 'react-icons/im';
import { MdEmail } from 'react-icons/md';
import { IPersonData } from '@th-types/person.type';
import { FiUser } from 'react-icons/fi';
import { FaLocationDot } from 'react-icons/fa6';
import { FaTicketAlt } from 'react-icons/fa';
import {
  ANNOUNCEMENT_THREAD_LABEL,
  DIRECT_THREAD_LABEL,
} from '@constants/directChat';

import './styles.css';
import { IoMdClose } from 'react-icons/io';

interface ChatHeaderProps {
  worker: IPersonData;
  showWorkerInfo: boolean;
  onViewProfile?: () => void;
  jobInfo?: {
    eventName: string;
    address: string;
    jobPosition: string;
  };
  isDirectMessage?: boolean;
  isAnnouncement?: boolean;
  onClose: () => void;
}

function ChatHeader({
  onViewProfile,
  worker,
  showWorkerInfo,
  jobInfo,
  isDirectMessage,
  isAnnouncement,
  onClose,
}: ChatHeaderProps) {
  const imageRef = useRef<HTMLImageElement>(null);
  // hook to use default avatar image if the src doesn't load
  useAvatarImageFallback(imageRef);

  let threadSubtitle = null;
  if (isAnnouncement) {
    threadSubtitle = (
      <ListGroup.Item>
        <small>
          <em>{ANNOUNCEMENT_THREAD_LABEL}&nbsp;</em>
        </small>
      </ListGroup.Item>
    );
  } else if (isDirectMessage) {
    threadSubtitle = (
      <ListGroup.Item>
        <small>
          <em>{DIRECT_THREAD_LABEL}&nbsp;</em>
        </small>
      </ListGroup.Item>
    );
  }

  return (
    <Stack direction="horizontal" className="chat-header">
      <Image
        ref={imageRef}
        src={worker.profileImageUrl}
        roundedCircle
        alt="Profile Image"
        style={{ width: 80, height: 80 }}
        className={isMobile ? 'mobile-img' : ''}
      />
      <ListGroup className="worker-info align-items-start truncate-text">
        <ListGroup.Item
          className="worker-fullname"
          onClick={onViewProfile}
          style={{ cursor: onViewProfile ? 'pointer' : 'default' }}
        >
          {worker?.fullName || `${worker.firstName} ${worker.lastName}`}
        </ListGroup.Item>
        {threadSubtitle}
        {!threadSubtitle && (
          <>
            {showWorkerInfo && worker?.email && (
              <>
                <ListGroup.Item className="worker-phone">
                  <ImPhone size={12} />
                  {worker?.cellPhone}
                </ListGroup.Item>
                <ListGroup.Item className="worker-email">
                  <MdEmail />
                  {worker?.email}
                </ListGroup.Item>
              </>
            )}
            {jobInfo && (
              <>
                <ListGroup.Item className="job-info-event-name">
                  <FaLocationDot />
                  {jobInfo.eventName}
                </ListGroup.Item>
                <ListGroup.Item className="job-info-address">
                  <FaTicketAlt />
                  {jobInfo.address}
                </ListGroup.Item>
              </>
            )}
          </>
        )}
      </ListGroup>

      <Stack className="header-actions ms-auto align-items-end gap-2">
        {isMobile && (
          <button onClick={onClose} className="ms-auto" type="button">
            <IoMdClose />
          </button>
        )}
        {onViewProfile && (
          <Button variant="light" onClick={onViewProfile}>
            {isMobile ? <FiUser size={20} /> : <b>VIEW PROFILE</b>}
          </Button>
        )}
        {/* VIDEO CHAT BUTTON IS IN DESIGN BUT NOT SUPPORTED YET */}
        {/* <Button variant="light">VIDEO CHAT</Button> */}
      </Stack>
    </Stack>
  );
}

export default ChatHeader;
